import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  CardContainer,
  CardHeader,
  Center,
  Divider,
  Spinner,
  Tag,
  Tooltip,
} from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { FlexViewPrimitive } from 'src/components/layout/FlexView/FlexView.styles';
import {
  EmptyState,
  FormInput,
  InformationBanner,
} from 'src/components/molecules';
import { downloadDynamicCaseImportTemplate } from 'src/components/pages/UploadCases/steps/dynamicCsvHelper/downloadDynamicCaseImportTemplate';
import {
  FormManagementQuestionnaireListItem,
  QuestionnaireStatus,
  useFormManagementQuestionnairesQuery,
  useFormManagementQuestionsQuery,
  useGetOrganisationCategoriesQuery,
} from 'src/graphql';
import { FormCategoryId } from 'src/graphql/formManagement/formCategories';
import { Template } from 'src/hooks/cases/useUploadCases';
import theme from 'src/theme/theme';
import { FeatureFlag, FormInputVariant, FormState, State } from 'src/types';
import { trackCaseImportReviewStartedEvent } from 'src/utils/analytics/track/caseImport';
import { StepContainer } from '../UploadCases.styles';
import { Title } from './CaseReview.styles';
import {
  CardBodyStyled,
  CardBodySubtitle,
  CardHeaderStyled,
  CardSection,
  CategoriesTagsWrapper,
  EmptyStateDescription,
  EmptyStateTitle,
  EmptyStateWrapper,
  InformationBannerContainer,
  QuestionCell,
  QuestionsList,
  QuestionWrapper,
} from './CategorySelection.styles';
import { buildDynamicCsvTemplateHeaders } from './dynamicCsvHelper/buildDynamicCsvTemplateHeaders';

const DEFAULT_LOCALE = 'en-GB';

const getUsableLocaleName = (name: unknown): string | undefined =>
  typeof name === 'string' && name.trim() ? name : undefined;

const resolveCategoryName = (locales: unknown): string | undefined => {
  const localeMap = locales as
    | Record<string, { name?: unknown } | undefined>
    | undefined;
  if (!localeMap) return undefined;

  const defaultLocaleName = getUsableLocaleName(
    localeMap[DEFAULT_LOCALE]?.name,
  );
  if (defaultLocaleName) {
    return defaultLocaleName;
  }

  return Object.values(localeMap)
    .map((locale) => getUsableLocaleName(locale?.name))
    .find((name): name is string => name !== undefined);
};

interface CategorySelectionProps {
  setShowUploadModal: (show: boolean) => void;
  setTemplate: (template: Template) => void;
  dynamicCategory: string;
  setDynamicCategory: (category: string) => void;
  dynamicFormId: string | null;
  setDynamicFormId: (formId: string | null) => void;
  stateProperties: {
    organisationId: string | null;
    userId: number | null;
    userSystemRole: string | null;
  };
}

export const CategorySelection = ({
  setShowUploadModal,
  setTemplate,
  dynamicCategory,
  setDynamicCategory,
  dynamicFormId,
  setDynamicFormId,
  stateProperties,
}: CategorySelectionProps) => {
  const features = useSelector((state: State) => state.features);
  const trackingEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const isDynamicQuestionsEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.DYNAMIC_QUESTIONS_V1,
  );
  const [dynamicCategoryForms, setDynamicCategoriesForms] = useState<
    FormManagementQuestionnaireListItem[]
  >([]);
  const [dynamicCategoryForm, setDynamicCategoryForm] = useState<string>('');
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [tooltipText, setTooltipText] = useState('');

  const {
    data: questionnairesData,
    isLoading,
    isFetching: isTableFetching,
    isError,
    refetch,
  } = useFormManagementQuestionnairesQuery(
    {
      filters: {
        status: QuestionnaireStatus.Active,
        isActive: true,
        formCategoryId: FormCategoryId.SPEAK_UP,
      },
    },
    { refetchOnWindowFocus: false },
  );

  const {
    data: questionsData,
    isLoading: isQuestionsLoading,
    isError: isQuestionsError,
    refetch: refetchQuestions,
  } = useFormManagementQuestionsQuery(
    { questionnaireId: dynamicFormId! },
    { enabled: !!dynamicFormId },
  );

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    refetch: refetchCategories,
  } = useGetOrganisationCategoriesQuery({
    isCOI: false,
  });

  if (!isDynamicQuestionsEnabled) {
    return null;
  }

  if (isLoading || isTableFetching || isCategoriesLoading) {
    return (
      <Center>
        <Spinner variant="secondary" size="large" />
      </Center>
    );
  }

  const questions =
    questionsData?.formManagementQuestions?.questions?.filter(
      (question) => question.questionType?.typeKey !== 'file_upload',
    ) || [];

  if (isError || isCategoriesError) {
    return (
      <Center>
        <EmptyState
          title="Unable to load forms"
          description="Something went wrong while loading forms for this category. Please try again."
          buttonText="Retry"
          onButtonClick={() => {
            refetch();
            refetchCategories();
          }}
          renderIconButton
          data-testid="category-details-error-state"
        />
      </Center>
    );
  }

  const categoryIdToLiveName = (
    categoriesData?.getOrganisationCategories ?? []
  ).reduce(
    (acc, category) => {
      const name = resolveCategoryName(category.locales);
      if (name) {
        acc[category.id] = name;
      }
      return acc;
    },
    {} as Record<number, string>,
  );

  const getLiveCategoryName = (q: FormManagementQuestionnaireListItem) =>
    (q.categoryId != null ? categoryIdToLiveName[q.categoryId] : undefined) ??
    q.category!;

  const defaultFlowQuestionnaire =
    questionnairesData?.formManagementQuestionnaires.filter(
      (questionnaire) =>
        questionnaire.internalName?.includes('misconduct_template') ||
        questionnaire.internalName?.includes('inquiry_template'),
    ) as FormManagementQuestionnaireListItem[];

  const defaultCategories = Array.from(
    new Set(defaultFlowQuestionnaire.map((q) => getLiveCategoryName(q))),
  );

  const dynamicFlowQuestionnaire =
    questionnairesData?.formManagementQuestionnaires.filter(
      (questionnaire) =>
        !questionnaire.internalName?.includes('misconduct_template') &&
        !questionnaire.internalName?.includes('inquiry_template'),
    ) as FormManagementQuestionnaireListItem[];

  const dynamicCategories = Array.from(
    new Set(dynamicFlowQuestionnaire.map((q) => getLiveCategoryName(q))),
  );

  const dynamicCategoriesWithForms = dynamicCategories.reduce(
    (acc, category) => {
      const forms = dynamicFlowQuestionnaire.filter(
        (questionnaire) => getLiveCategoryName(questionnaire) === category,
      );
      acc[category] = forms;
      return acc;
    },
    {} as Record<string, FormManagementQuestionnaireListItem[]>,
  );

  const onChangeCategory = (value: string) => {
    setDynamicCategory(value);
    setDynamicCategoriesForms(dynamicCategoriesWithForms[value]);
    if (dynamicCategoriesWithForms[value]?.length === 1) {
      setDynamicCategoryForm(
        dynamicCategoriesWithForms[value][0].publicTitle ??
          dynamicCategoriesWithForms[value][0].internalName!,
      );
      setDynamicFormId(dynamicCategoriesWithForms[value][0].id!);
    } else {
      setDynamicCategoryForm('');
      setDynamicFormId(null);
    }
  };

  const onChangeForm = (value: string) => {
    setDynamicCategoryForm(value);
    const selectedForm = dynamicCategoriesWithForms[dynamicCategory]?.find(
      (form) => form.publicTitle === value || form.internalName === value,
    );
    setDynamicFormId(selectedForm ? selectedForm.id! : null);
  };

  const disabledUploadDynamic = !dynamicCategory || !dynamicCategoryForm;
  const disabledDownloadDynamicTemplate =
    disabledUploadDynamic || isQuestionsLoading || isQuestionsError;

  const handleDownloadDynamicTemplate = () => {
    const headers = buildDynamicCsvTemplateHeaders(questions);

    downloadDynamicCaseImportTemplate(
      headers,
      `${dynamicCategoryForm}-import-template`,
    );
  };

  const warningBannerTitle = dynamicCategories.length
    ? 'Headers must match question titles'
    : 'No custom categories available';
  const warningBannerDescription = dynamicCategories.length
    ? 'When uploading cases to custom categories, please ensure that the headers in your import file match the question titles in the form you are uploading to, including the case details data.'
    : 'There are no active custom categories with forms available. Please create an active custom question category to continue with this option.';

  const relatedQuestionsEmptyState = (
    title: string,
    description: string,
    isLoading?: boolean,
    onButtonClick?: () => void,
  ) => (
    <EmptyStateWrapper>
      <StyledIcon
        className="uil uil-file-question"
        size="60px"
        color={theme.atlas.core.color.gray[300]}
      />
      <EmptyStateTitle>{title}</EmptyStateTitle>
      <EmptyStateDescription>{description}</EmptyStateDescription>
      {onButtonClick && (
        <Button
          variant="primary"
          size="small"
          onClick={onButtonClick}
          loading={isLoading}
          data-testid="retry-load-questions-button"
        >
          {!isLoading ? (
            <StyledIconInButton
              size="small"
              position="left"
              className="uil uil-refresh"
            />
          ) : (
            <Spinner size="small" variant="secondary" />
          )}{' '}
          Retry
        </Button>
      )}
    </EmptyStateWrapper>
  );

  const showRelatedQuestions = () => {
    if (isQuestionsError) {
      return relatedQuestionsEmptyState(
        'Unable to load questions',
        'Something went wrong while loading questions for the selected form. Please try again.',
        isQuestionsLoading,
        refetchQuestions,
      );
    } else if (questions.length === 0) {
      return relatedQuestionsEmptyState(
        'No questions available',
        'There are no questions available for the selected form. Update the form or select another one to upload your cases to.',
      );
    } else if (questions.length) {
      return (
        <>
          <InformationBanner
            $fullWidth
            state={FormState.DEFAULT}
            title="Mapping fields"
            description='Use "Add new" button and choose "Custom question field" option when importing your file to map the questions headers in the form.'
            data-testid="warning-banner"
          />
          <QuestionsList>
            <QuestionWrapper data-testid="question-header">
              <QuestionCell $size="4" $header={true}>
                Question Title
              </QuestionCell>
              <QuestionCell $size="2" $header={true}>
                Type
              </QuestionCell>
              <QuestionCell $size="1" $header={true}>
                Required
              </QuestionCell>
            </QuestionWrapper>
            {questions.map((question) => (
              <QuestionWrapper key={question.id} data-testid="question">
                <QuestionCell $size="4">
                  {question.questionText}
                  {question.options && question.options?.length > 0 && (
                    <StyledIcon
                      size="24px"
                      color={theme.atlas.semantic.color.type.muted}
                      className="uil uil-info-circle"
                      onMouseOver={(e) => {
                        setRoot(e.currentTarget);
                        setTooltipVisible(true);
                        setTooltipText(
                          `Available options: ${question.options
                            ?.map((option) => option.optionText)
                            .join(', ')}`,
                        );
                      }}
                      onMouseOut={() => setTooltipVisible(false)}
                      data-testid={`StyledIcon-info-${question.questionText}`}
                    />
                  )}
                </QuestionCell>
                <QuestionCell $size="2">
                  {question.questionType?.typeKey?.replaceAll('_', ' ')}
                </QuestionCell>
                <QuestionCell $size="1" $center={true}>
                  {question.isRequired ? 'V' : '-'}
                </QuestionCell>
              </QuestionWrapper>
            ))}
          </QuestionsList>
          {tooltipVisible && (
            <Tooltip
              text={tooltipText}
              variant="down"
              root={root as HTMLElement}
            />
          )}
        </>
      );
    } else {
      return relatedQuestionsEmptyState(
        'Select a category and form to see related questions',
        'Please select a custom category and a form to see the related questions that will help you map the fields in your import file.',
      );
    }
  };

  return (
    <StepContainer>
      <Title data-testid="case-import-category-select-title">
        Select the relevant flow to upload your cases
      </Title>
      <CardContainer>
        <CardHeader
          hasDivider
          startAccessory={
            <CardHeaderStyled>Upload to default categories</CardHeaderStyled>
          }
          endAccessory={
            <FlexViewPrimitive $gap={2}>
              <Button
                data-testid="upload-default-categories-button"
                variant="primary"
                size="small"
                onClick={() => {
                  setShowUploadModal(true);
                  setTemplate(Template.CASES_UPLOAD);
                  if (trackingEnabled && stateProperties.organisationId) {
                    trackCaseImportReviewStartedEvent({
                      organisation_id: Number(stateProperties.organisationId),
                      user_id: String(stateProperties.userId),
                      user_system_role: stateProperties.userSystemRole,
                      is_default_questions_flow: true,
                    });
                  }
                }}
              >
                Upload to default categories
              </Button>
            </FlexViewPrimitive>
          }
        />
        <CardBodyStyled>
          <CardSection>
            <CardBodySubtitle>
              Default categories are based on our standard case templates. If
              your cases fit into one of these templates (misconduct \ inquiry),
              select this flow to upload your cases. If not, you can select from
              custom categories below and upload to the relevant form.
            </CardBodySubtitle>
          </CardSection>
          <CardSection>
            <CategoriesTagsWrapper>
              {defaultCategories.map((category) => (
                <Tag
                  key={category}
                  text={category}
                  hideCloseIcon={true}
                  data-testid="tag"
                />
              ))}
            </CategoriesTagsWrapper>
          </CardSection>
        </CardBodyStyled>
      </CardContainer>

      <CardContainer>
        <CardHeader
          hasDivider
          startAccessory={
            <CardHeaderStyled>Upload to custom category</CardHeaderStyled>
          }
          endAccessory={
            <FlexViewPrimitive $gap={2}>
              <Button
                data-testid="download-dynamic-template-button"
                variant="secondary"
                size="small"
                onClick={handleDownloadDynamicTemplate}
                disabled={disabledDownloadDynamicTemplate}
              >
                Download csv template
              </Button>
              <Button
                data-testid="upload-dynamic-category-button"
                variant="primary"
                size="small"
                onClick={() => {
                  setShowUploadModal(true);
                  setTemplate(Template.CASES_UPLOAD_DYNAMIC);
                  if (trackingEnabled && stateProperties.organisationId) {
                    trackCaseImportReviewStartedEvent({
                      organisation_id: Number(stateProperties.organisationId),
                      user_id: String(stateProperties.userId),
                      user_system_role: stateProperties.userSystemRole,
                      is_dynamic_questions_flow: true,
                    });
                  }
                }}
                disabled={disabledUploadDynamic}
              >
                {disabledUploadDynamic
                  ? 'Upload to custom category'
                  : `Upload to "${dynamicCategoryForm.length < 20 ? dynamicCategoryForm : `${dynamicCategoryForm.slice(0, 20)}...`}"`}
              </Button>
            </FlexViewPrimitive>
          }
        />
        <>
          <InformationBannerContainer>
            <InformationBanner
              $fullWidth
              state={FormState.WARNING}
              title={warningBannerTitle}
              description={warningBannerDescription}
              data-testid="warning-banner"
            />
          </InformationBannerContainer>
          <CardBodyStyled>
            <CardSection>
              <FormInput
                isRequired={false}
                label="Category"
                data-testid="case-import-category-input"
                variant={FormInputVariant.DROPDOWN}
                state={FormState.DEFAULT}
                options={dynamicCategories.map((value) => ({
                  value,
                  label: value,
                }))}
                value={
                  dynamicCategory
                    ? { label: dynamicCategory, value: dynamicCategory }
                    : null
                }
                onChange={(option) => {
                  if (option && !Array.isArray(option) && 'label' in option) {
                    onChangeCategory(option.value as string);
                  }
                }}
                placeholder="Choose the category"
              />
              <FormInput
                isRequired={false}
                label="Related form"
                data-testid="case-import-form-input"
                variant={FormInputVariant.DROPDOWN}
                state={FormState.DEFAULT}
                options={dynamicCategoryForms.map((value) => ({
                  value: value.publicTitle ?? value.internalName!,
                  label: value.publicTitle ?? value.internalName!,
                }))}
                value={
                  dynamicCategoryForm
                    ? { label: dynamicCategoryForm, value: dynamicCategoryForm }
                    : null
                }
                onChange={(option) => {
                  if (option && !Array.isArray(option) && 'label' in option) {
                    onChangeForm(option.value as string);
                  }
                }}
                placeholder="Choose the form to upload to"
              />
            </CardSection>
            <Divider
              variant="vertical"
              color={theme.atlas.semantic.color.ui.divider.default}
              height="auto"
            />
            <CardSection>
              {isQuestionsLoading ? (
                <Center>
                  <Spinner variant="secondary" size="medium" />
                </Center>
              ) : (
                showRelatedQuestions()
              )}
            </CardSection>
          </CardBodyStyled>
        </>
      </CardContainer>
    </StepContainer>
  );
};
